// Sous-plan 10, lot B : le chat prend les couleurs de l'app qui l'heberge.
// En mode integre, l'accent vient du document parent (variable --dls-accent de l'app, meme origine)
// et le theme clair ou sombre suit son attribut data-theme ; en visite directe, l'accent vient du
// build (NEXT_PUBLIC_APP_ACCENT) et le theme reste celui de Dust. L'echelle « highlight » de Sparkle
// est recalculee en oklch a partir d'une seule couleur, puis posee en style inline sur <html> :
// cela prime sur :root et .dark de tokens.css sans toucher a Sparkle. Sans accent lisible, rien
// n'est pose et le chat garde les couleurs de Dust.
import type { FenetreMinimale } from "@app/lib/compta/mode_integre";
import { estIntegre } from "@app/lib/compta/mode_integre";

export type Oklch = { l: number; c: number; h: number };

export interface FenetreHabillage extends FenetreMinimale {
  parent?: { document?: { documentElement?: Element | null } } | null;
  getComputedStyle?: (e: Element) => { getPropertyValue(nom: string): string };
}

export interface DocumentHabillage {
  documentElement: {
    classList: {
      toggle(c: string, on: boolean): unknown;
      contains(c: string): boolean;
    };
    style: {
      setProperty(k: string, v: string): unknown;
      removeProperty(k: string): unknown;
    };
  };
}

export type ThemeParent = "light" | "dark";

const HEX = /^#([0-9a-f]{6})$/i;

function lineaire(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

// sRGB → OKLab → OKLCH (Bjorn Ottosson), la meme base que tokens.css.
export function hexVersOklch(hex: string): Oklch | null {
  const m = HEX.exec(hex.trim());
  if (!m) {
    return null;
  }
  const n = Number.parseInt(m[1], 16);
  const r = lineaire((n >> 16) & 255);
  const g = lineaire((n >> 8) & 255);
  const b = lineaire(n & 255);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const mm = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * mm - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * mm + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * mm - 0.808675766 * s;
  const c = Math.hypot(a, bb);
  const h = c < 1e-4 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

// Reference : l'echelle « pink » de tokens.css (clarte en 0..1, chroma relative au pas 500).
const PAS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
const L_REF = [
  0.9708, 0.9281, 0.8789, 0.7961, 0.7197, 0.6511, 0.5893, 0.5316, 0.4699,
  0.4067, 0.2312,
];
const C_REL = [
  0.083, 0.215, 0.377, 0.595, 0.808, 1, 0.926, 0.85, 0.772, 0.693, 0.395,
];
const INDICE_500 = 5;

function chaine({ l, c, h }: Oklch): string {
  return `oklch(${(l * 100).toFixed(2)}% ${c.toFixed(4)} ${h.toFixed(2)})`;
}

export function echelleAccent(
  hex: string,
  sombre: boolean
): Record<string, string> | null {
  const base = hexVersOklch(hex);
  if (!base) {
    return null;
  }
  const decalage = base.l - L_REF[INDICE_500];
  const clair = L_REF.map((l, i) => ({
    l: Math.min(0.985, Math.max(0.08, l + decalage)),
    c: base.c * C_REL[i],
    h: base.h,
  }));
  // En sombre, tokens.css inverse l'echelle (50 = le plus fonce) ; le pas 500 reste la couleur meme.
  const pas = sombre ? [...clair].reverse() : clair;
  const jetons: Record<string, string> = {};
  PAS.forEach((p, i) => {
    jetons[`--color-highlight-${p}`] = chaine(pas[i]);
  });
  jetons["--color-highlight-muted"] = chaine({
    l: 0.7487,
    c: Math.min(base.c, 0.0618),
    h: base.h,
  });
  // Anneau de focus : tokens.css prend le primitif 400 en clair, 600 en sombre.
  jetons["--color-border-focus"] = chaine(clair[sombre ? 6 : 4]);
  // Sparkle peint son bouton highlight en sombre avec les primitifs blue-500/600 (Button.tsx :
  // dark:from-blue-500 dark:to-blue-600) et l'anneau avec blue-500 : la meme couleur, quel que
  // soit le theme, pour que ces deux-la suivent l'accent aussi.
  jetons["--color-blue-500"] = chaine(clair[INDICE_500]);
  jetons["--color-blue-600"] = chaine(clair[INDICE_500 + 1]);
  jetons["--color-ring"] = chaine(clair[INDICE_500]);
  return jetons;
}

const JETONS = [
  ...PAS.map((p) => `--color-highlight-${p}`),
  "--color-highlight-muted",
  "--color-border-focus",
  "--color-blue-500",
  "--color-blue-600",
  "--color-ring",
];

function racineDuParent(fenetre: FenetreHabillage): Element | null {
  try {
    return fenetre.parent?.document?.documentElement ?? null;
  } catch {
    // Lire le parent peut lever dans un cadre d'origine etrangere : rien a lire.
    return null;
  }
}

export function accentDuParent(
  fenetre: FenetreHabillage | undefined
): string | null {
  if (!fenetre || !estIntegre(fenetre)) {
    return null;
  }
  try {
    const racine = racineDuParent(fenetre);
    const lire = fenetre.getComputedStyle;
    if (!racine || !lire) {
      return null;
    }
    const v = lire(racine).getPropertyValue("--dls-accent").trim();
    return HEX.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function themeDuParent(
  fenetre: FenetreHabillage | undefined
): ThemeParent | null {
  if (!fenetre || !estIntegre(fenetre)) {
    return null;
  }
  try {
    const t = racineDuParent(fenetre)?.getAttribute("data-theme");
    return t === "dark" || t === "light" ? t : null;
  } catch {
    return null;
  }
}

// Suit les bascules de theme de l'app : rappel a chaque changement de data-theme du parent.
export function observerThemeDuParent(
  fenetre: FenetreHabillage | undefined,
  rappel: (theme: ThemeParent) => void
): () => void {
  if (!fenetre || !estIntegre(fenetre)) {
    return () => {};
  }
  try {
    const racine = racineDuParent(fenetre);
    if (!racine || typeof MutationObserver === "undefined") {
      return () => {};
    }
    const obs = new MutationObserver(() => {
      const t = themeDuParent(fenetre);
      if (t) {
        rappel(t);
      }
    });
    obs.observe(racine, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  } catch {
    return () => {};
  }
}

let accentRetenu: string | null = null;

export function appliquerAccent(
  doc: DocumentHabillage,
  hex: string | null,
  sombre: boolean
): void {
  const jetons = hex ? echelleAccent(hex, sombre) : null;
  const style = doc.documentElement.style;
  if (!jetons) {
    for (const j of JETONS) {
      style.removeProperty(j);
    }
    return;
  }
  for (const [j, v] of Object.entries(jetons)) {
    style.setProperty(j, v);
  }
}

// A appeler avant le premier rendu : le theme du parent (pas d'eclair clair dans une app sombre)
// et l'accent, du parent d'abord, du build sinon.
export function habillerAuDemarrage(
  fenetre: FenetreHabillage | undefined,
  doc: DocumentHabillage,
  accentDuBuild: string | undefined
): void {
  const theme = themeDuParent(fenetre);
  if (theme) {
    doc.documentElement.classList.toggle("dark", theme === "dark");
  }
  const duBuild = accentDuBuild?.trim() ?? "";
  accentRetenu =
    accentDuParent(fenetre) ?? (HEX.test(duBuild) ? duBuild : null);
  appliquerAccent(
    doc,
    accentRetenu,
    doc.documentElement.classList.contains("dark")
  );
}

// A chaque bascule de theme : la meme couleur, l'echelle du bon theme.
export function rehabiller(doc: DocumentHabillage, sombre: boolean): void {
  appliquerAccent(doc, accentRetenu, sombre);
}
